import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDirtyTracker } from '../../../../context/UpdateContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormControlLabel, Modal } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ProductChangeHistoryModal from '../../common/ProductChangeHistoryModal';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { GridCloseIcon } from '@mui/x-data-grid';
import ProductPickerModal from '../../common/ProductPickerModal';

import { createPass, getPassById, updatePass, deletePassById, changeStatusPass } from '../../../../api/ticket';
import { togglePassExposure } from '../../../../api/exposure';
import { FAVORITE_TARGET } from '../../../../api/favorite';
import FavoriteStar from '../../../common/FavoriteStar';
import useFavorite from '../../../../utils/useFavorite';
import { getDirectBranches } from '../../../../api/point';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import { centersDetail } from '../../../../api/store-management';
import { getBranchSubscriptionEligibility } from '../../../../api/subscription';
import { errorMessages, imageValidation, validateDateRange, sellStatus } from '../../../../utils/methods';
import { appendField, appendFields } from '../../../../utils/voucherFormData';

import IOSSwitch from '../../IosSwitch';
import useUserAuth from '../../../../utils/useUserAuth';
import BannerDeleteModal from '../../../banner/BannerDeleteModal';
import PassEditStep01 from './PassEditStep01';
import PassEditStep02 from './PassEditStep02';

import {
  PageContainer,
  Header,
  HeaderTitle,
  Divider,
  EditModeHeader,
  StatusToggleWrapper,
  StatusToggleLabel,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  PassInfoWrapper,
  PassInfoHeader,
  PassInfoTitle,
  StatusBadge,
  CopyButton,
  LoadProductButton,
  HeaderActionGroup,
  CopyModeBanner,
  CopyModeBannerTitle,
  CopyModeBannerText,
  WishlistWrapper,
  WishlistIcon,
  WishlistText,
  ModalContainer,
  ModalCloseIcon,
  ModalIconBadge,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalDescText,
  ModalDescTextSub,
  ModalButtonWrapper,
  ModalCancelButton,
  ModalConfirmButton,
  StepIndicatorContainer,
  StepTabsWrapper,
  StepTab,
  StepTabShapeFirst,
  StepTabShapeSecond,
  StepTabContent,
  StepTabNumber,
  StepTabLabel,
  BackToListLink,
  BackToListIcon,
  BackToListText,
  SubBlockModalContainer,
  SubBlockCloseButton,
  SubBlockIconWrapper,
  SubBlockTitle,
  SubBlockBody,
  SubBlockBodyText,
  SubBlockEmphasis,
  SubBlockConfirmButton,
} from './PassEditStyled';

// 상수 정의
const DAYS = [
  { label: '월', value: 'MON' },
  { label: '화', value: 'TUE' },
  { label: '수', value: 'WED' },
  { label: '목', value: 'THU' },
  { label: '금', value: 'FRI' },
  { label: '토', value: 'SAT' },
  { label: '일', value: 'SUN' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

const PassEdit = () => {
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // 복사: /pass/create?copyFrom={id} — 편집 로더를 그대로 재사용한다
  const copyFromId = searchParams.get('copyFrom');
  const user = useSelector(state => state.user.user);

  const isEditMode = !!id;
  const isCopyMode = !isEditMode && !!copyFromId;
  const [legacyBranchNames, setLegacyBranchNames] = useState({});

  // 같은 본사 산하 직영점 목록 (#73 다지점 공유 — 지점 계정도 본사 직영점 전체 수신)
  const { data: directBranches = [] } = useQuery({
    queryKey: ['directBranches', user?.branch?.branchId],
    queryFn: () => getDirectBranches(user.token, user.branch?.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 생성 모드 기본 판매·이용지점 = 로그인 지점 (#73)
  useEffect(() => {
    if (!isEditMode && user?.branch?.branchId) {
      if (!getValues('saleBranchIds')?.length) setValue('saleBranchIds', [user.branch.branchId]);
      if (getValues('useSaleBranchYn') !== 'Y' && !getValues('useBranchIds')?.length) setValue('useBranchIds', [user.branch.branchId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, user?.branch?.branchId]);
  const canCreate = useUserAuth('TICKET_PASS_CREATE');
  const canEdit = useUserAuth('TICKET_PASS_EDIT');
  const canSave = isEditMode ? canEdit : canCreate;

  // 지점별 sportTypes 조회 (centerId로 조회)
  const { data: centrerInfo } = useQuery({
    queryKey: ['centerInfo', user?.branch?.centerId],
    queryFn: () => centersDetail(user?.token, user?.branch?.centerId),
    enabled: !!user?.branch?.centerId,
  });

  // 지점의 구독 기능 승인 여부 — 화면 진입 시 1회 조회 후 캐시
  const { data: subscriptionEligibility } = useQuery({
    queryKey: ['subscriptionEligibility', user?.branch?.branchId],
    queryFn: () => getBranchSubscriptionEligibility(user?.branch?.branchId, user?.token),
    enabled: !!user?.branch?.branchId,
    staleTime: 5 * 60 * 1000, // 5분 캐시 — 매 렌더 재조회 방지
  });

  // Step 상태
  const [currentStep, setCurrentStep] = useState(1);

  // 기본 상태
  const [loader, setLoader] = useState(false);
  const [passData, setPassData] = useState({
    voucherList: [],
    regularPrice: 0,
    discountRate: 0,
    finalPrice: 0,
    saleStatus: false,
  });

  // Step01 상태
  const [additionalProductSettings, setAdditionalProductSettings] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSettings, setTimeSettings] = useState({
    startHour: '00',
    startMinute: '00',
    endHour: '00',
    endMinute: '00',
  });
  const [usagePeriodType, setUsagePeriodType] = useState('MONTH');
  const [isTaxFree, setIsTaxFree] = useState(false);
  const [discountType, setDiscountType] = useState('%');
  const [hasDiscountEndDate, setHasDiscountEndDate] = useState(false);
  const [appExposure, setAppExposure] = useState(true);

  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);

  const handleExposureToggle = async () => {
    const newValue = !appExposure;
    const saleStatus = watch('saleStatus');
    if (newValue && (saleStatus === 'EXPIRED' || saleStatus === 'SALE_END')) {
      setExposureAlertOpen(true);
      return;
    }
    setAppExposure(newValue);
    try {
      await togglePassExposure(user.token, id, newValue ? 'Y' : 'N');
      toast.success(newValue ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      setAppExposure(!newValue);
      toast.error(errorMessages(error));
    }
  };

  const [usageType, setUsageType] = useState('PERIOD');
  const [productType, setProductType] = useState('GENERAL'); // 'GENERAL' | 'SUBSCRIPTION' (구독은 월간 고정)
  // 입장 가능 출입문 — 선택된 출입문(BranchDoor) id 목록. 미선택(빈 배열)이면 accessGroupIds 를 전송하지 않는다.
  const [selectedDoorIds, setSelectedDoorIds] = useState([]);
  // 출입문 체계 전환 지점 여부 — 전환 지점은 저장 시 항상 accessGroupIds 를 전송한다.
  const [doorBranchConverted, setDoorBranchConverted] = useState(false);

  // Step02 상태
  const [aiToggle, setAiToggle] = useState(false);
  const [curriculumList, setCurriculumList] = useState([]);

  // Modal 상태
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  // 지점 공용 즐겨찾기(상세 헤더 별표)
  const [favorite, setFavorite] = useFavorite(FAVORITE_TARGET.PASS, id, isEditMode);
  const [copySourceName, setCopySourceName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // 구독 미승인 지점 안내 팝업
  const [subscriptionBlockModalOpen, setSubscriptionBlockModalOpen] = useState(false);

  const defaultValues = useMemo(() => {
    const defaults = {
      sports: passData?.sportTypes ?? passData?.sportType,
      // 중지권 기본값: 무제한
      stopType: 'UNLIMITED',
    };
    return defaults;
  }, [passData]);

  const { handleSubmit, control, setValue, getValues, reset, watch, formState } = useForm({ defaultValues });
  useDirtyTracker(formState.isDirty);

  // ============================================
  // Data Fetching (Edit Mode)
  // ============================================

  const getPass = async (sourceId, forCopy = false) => {
    try {
      const data = await getPassById(sourceId, user.token);
      setPassData({
        ...data,
        voucherList: data.images?.map(item => ({ voucherUrl: item })) || [],
      });

      reset(data);
      setSelectedDoorIds((data?.accessGroups ?? []).map(g => g.id));
      // #73 판매·이용지점 프리필 — 비공유 상품은 소유 지점 1개
      {
        const fallbackIds = data?.participatingBranchIds?.length
          ? data.participatingBranchIds
          : [data?.branchId ?? user?.branch?.branchId].filter(Boolean);
        setValue('saleBranchIds', data?.saleBranchIds?.length ? data.saleBranchIds : fallbackIds);
        // #98 소속지점 모드면 이용지점 목록은 비어 있는 게 정상
        setValue('useSaleBranchYn', data?.useSaleBranchYn === 'Y' ? 'Y' : 'N');
        setValue('useBranchIds', data?.useSaleBranchYn === 'Y' ? [] : (data?.useBranchIds?.length ? data.useBranchIds : fallbackIds));
      }
      // #73 판매/이용 옵션(직영점 목록)에 없는 참여지점(과거 가맹점 데이터 등) 이름 폴백용
      setLegacyBranchNames(
        (data?.participatingBranchIds || []).reduce((acc, bid, idx) => {
          acc[bid] = data.participatingBranchNames?.[idx] ?? `지점 #${bid}`;
          return acc;
        }, {}),
      );
      setAdditionalProductSettings(
        data?.products?.map(el => ({
          name: el.name,
          price: el.price,
          use: el.status === 'USED',
        })) || [],
      );

      setValue('sports', data?.sportTypes ?? data?.sportType);

      // 중지권 매핑: null=무제한, 0=중지불가, 양수=직접입력
      if (data?.stopCount !== null && data?.stopCount !== undefined && data?.stopCount !== '') {
        setValue('stopCount', data?.stopCount);
        setValue('stopPeriod', data?.stopPeriod);
        setValue('stopType', Number(data?.stopCount) > 0 ? 'CUSTOM' : 'DISABLED');
      } else {
        setValue('stopType', 'UNLIMITED');
      }

      if (data?.cancelCount) {
        setValue('cancelCount', data?.cancelCount);
        setValue('cancelCounts', false);
      } else {
        setValue('cancelCounts', true);
      }

      if (data?.reservationCount) {
        setValue('reservationCount', data?.reservationCount);
        setValue('reservationCounts', false);
      } else {
        setValue('reservationCounts', true);
      }

      // 할인 타입 설정 (RATE → %, AMOUNT → 원)
      if (data?.discountType) {
        setDiscountType(data.discountType === 'AMOUNT' ? '원' : '%');
      }

      setUsagePeriodType(data?.usagePeriodType || 'MONTH');
      // usageCount 값이 있으면 횟수제, 없으면 기간제
      if (data?.usageCount) {
        setUsageType('COUNT');
        setValue('usageCount', data?.usageCount);
      } else {
        setUsageType('PERIOD');
      }

      // 구독제 여부 로드 (구독은 월간 고정)
      setProductType(data?.subscriptionYn ? 'SUBSCRIPTION' : 'GENERAL');
      setAppExposure(data?.exposureYn !== 'N');
      setValue('hasSaleEndDate', !data?.hasSaleEndDate);

      // 요일 설정 로드 (allowedDays: "MON,TUE,WED" 형식) - 중복 제거
      if (data?.allowedDays) {
        const days = data.allowedDays.split(',').filter(d => d.trim());
        const uniqueDays = [...new Set(days)];
        setSelectedDays(uniqueDays);
      }

      // 시간 설정 로드 (startTime: "09:00:00", endTime: "18:00:00" 형식)
      if (data?.startTime) {
        const [startHour, startMinute] = data.startTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          startHour: startHour || '00',
          startMinute: startMinute || '00',
        }));
      }
      if (data?.endTime) {
        const [endHour, endMinute] = data.endTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          endHour: endHour || '00',
          endMinute: endMinute || '00',
        }));
      }

      // 복사본은 이름만 비우고(원본명은 placeholder 로 안내) 미노출 상태로 시작한다
      if (forCopy) {
        setCopySourceName(data?.passName ?? '');
        setValue('passName', '');
        setAppExposure(false);
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 편집(:id) / 복사(?copyFrom) 모두 같은 로더를 쓴다
  useEffect(() => {
    const sourceId = id ?? copyFromId;
    if (sourceId) {
      getPass(sourceId, isCopyMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, copyFromId]);

  // ============================================
  // Product Type Handler — 구독 선택 시 eligibility 검사
  // ============================================

  const handleProductTypeChange = newType => {
    if (newType === 'SUBSCRIPTION' && subscriptionEligibility?.enabled !== true) {
      // 미승인 지점(enabled=false) 또는 eligibility 아직 미로드(enabled=undefined):
      // 팝업 표시, productType은 GENERAL 유지
      setSubscriptionBlockModalOpen(true);
      return;
    }
    setProductType(newType);
  };

  const handleSubscriptionBlockClose = () => {
    // 팝업 닫을 때 라디오 시각 상태를 productType(GENERAL)에 명시적으로 맞춤
    setProductType('GENERAL');
    setSubscriptionBlockModalOpen(false);
  };

  // ============================================
  // Day Toggle Handler
  // ============================================

  const handleDayToggle = day => {
    setSelectedDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  // ============================================
  // Time Change Handler
  // ============================================

  const handleTimeChange = (field, value) => {
    setTimeSettings(prev => ({ ...prev, [field]: value }));
  };

  // ============================================
  // Product Handlers
  // ============================================

  const handleAddProduct = product => {
    setAdditionalProductSettings(prev => [
      ...prev,
      { name: product.productName, price: product.price, productId: product.productId },
    ]);
  };

  const handleRemoveProduct = index => {
    setAdditionalProductSettings(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // Image Handlers
  // ============================================

  const uploadImages = async e => {
    const files = e.target.files;
    const currentLength = [...passData.voucherList, ...files].length;
    const imageLimit = 7;

    if (currentLength > imageLimit) {
      alert(`You can only upload up to ${imageLimit} images.`);
      return;
    }

    const images = [];
    for (const file of files) {
      try {
        const loadedImage = await imageValidation(file);
        images.push({ voucherImg: loadedImage });
      } catch (error) {
        toast.error(errorMessages(error));
      }
    }

    setPassData({
      ...passData,
      voucherList: [...passData.voucherList, ...images],
    });
  };

  const deleteVoucher = index => {
    setPassData({
      ...passData,
      voucherList: passData.voucherList.filter((_, i) => i !== index),
    });
  };

  // ============================================
  // Curriculum Handlers
  // ============================================

  const handleCurriculumChange = (index, field, value) => {
    setCurriculumList(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleCurriculumImageUpload = async (curriculumIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const loadedImage = await imageValidation(file);
      setCurriculumList(prev =>
        prev.map((item, i) => {
          if (i === curriculumIndex) {
            const images = item.images || [];
            if (images.length >= 7) {
              toast.error('이미지는 최대 7장까지 업로드 가능합니다.');
              return item;
            }
            return { ...item, images: [...images, { file: loadedImage }] };
          }
          return item;
        }),
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleCurriculumImageDelete = (curriculumIndex, imageIndex) => {
    setCurriculumList(prev =>
      prev.map((item, i) => {
        if (i === curriculumIndex) {
          return { ...item, images: item.images.filter((_, imgI) => imgI !== imageIndex) };
        }
        return item;
      }),
    );
  };

  const addCurriculum = () => {
    if (curriculumList.length >= 5) {
      toast.error('커리큘럼은 최대 5개까지 등록 가능합니다.');
      return;
    }
    setCurriculumList(prev => [...prev, { title: '', description: '', images: [] }]);
  };

  const deleteCurriculum = index => {
    setCurriculumList(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // Price Calculation
  // ============================================

  // 콤마 포함 문자열/숫자를 숫자로 변환 (빈값·null 은 0)
  const toNumber = value => Number(value?.toString().replace(/,/g, '') || 0);

  // 판매금액/할인/결제금액은 서로 연동된다. 과거에는 [lastChangedField] effect + watch() 조합으로
  // 재계산했는데, 연속된 키 입력이 누락돼 결제금액이 첫 자리만 반영되는 버그가 있었다.
  // 이제 각 입력 onChange 핸들러에서 getValues()로 최신 값을 읽어 동기적으로 재계산한다.

  // 판매금액 변경 → 결제금액 = 판매금액, 할인 초기화
  const handleRegularPriceChange = () => {
    const regularPriceNum = toNumber(getValues('regularPrice'));
    // 판매금액을 비우면(0) 결제금액만 0으로 리셋해 잔존값("판매금액 0인데 결제금액 40,000")을 막는다.
    // 할인은 건드리지 않는다 — 새 판매금액을 입력하면 아래에서 다시 0으로 초기화되므로, 값을 지웠다
    // 다시 입력하는 도중 직전 할인율이 매번 날아가지 않도록 한다.
    if (regularPriceNum <= 0) {
      setValue('finalPrice', 0);
      return;
    }
    setValue('finalPrice', regularPriceNum);
    setValue('discountRate', 0);
  };

  // 할인 변경 → 결제금액 재계산 (% 또는 원)
  const handleDiscountRateChange = () => {
    const regularPriceNum = toNumber(getValues('regularPrice'));
    const discountRateNum = toNumber(getValues('discountRate'));
    if (regularPriceNum <= 0) return;

    if (discountRateNum < 0) {
      toast.error(discountType === '원' ? '할인금액은 음수가 될 수 없습니다' : '할인율은 음수가 될 수 없습니다');
      return;
    }
    if (discountType === '%') {
      if (discountRateNum > 100) {
        toast.error('할인율은 100%를 초과할 수 없습니다');
        return;
      }
      setValue('finalPrice', Math.round(regularPriceNum - (regularPriceNum * discountRateNum) / 100));
    } else {
      if (discountRateNum > regularPriceNum) {
        toast.error('할인금액이 정상가보다 클 수 없습니다');
        return;
      }
      setValue('finalPrice', regularPriceNum - discountRateNum);
    }
  };

  // 결제금액 직접 변경 → 할인 역산
  const handleFinalPriceChange = () => {
    const regularPriceNum = toNumber(getValues('regularPrice'));
    const finalPriceNum = toNumber(getValues('finalPrice'));
    if (regularPriceNum <= 0) return;

    if (finalPriceNum > regularPriceNum) {
      toast.error('최종가는 정상가보다 클 수 없습니다');
      return;
    }
    if (finalPriceNum < 0) {
      toast.error('최종가는 음수가 될 수 없습니다');
      return;
    }
    if (discountType === '%') {
      let calculatedDiscountRate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
      if (finalPriceNum > 0 && calculatedDiscountRate >= 100) {
        calculatedDiscountRate = 99;
      }
      setValue('discountRate', calculatedDiscountRate);
    } else {
      setValue('discountRate', regularPriceNum - finalPriceNum);
    }
  };

  // 단위 전환 시 기존 값 변환 (% ↔ 원)
  useEffect(() => {
    const regularPriceNum = toNumber(getValues('regularPrice'));
    const finalPriceNum = toNumber(getValues('finalPrice'));
    if (regularPriceNum <= 0) return;

    if (discountType === '원') {
      setValue('discountRate', Math.max(0, regularPriceNum - finalPriceNum));
    } else {
      const rate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
      setValue('discountRate', Math.max(0, Math.min(rate, 99)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountType]);

  // ============================================
  // Step Navigation
  // ============================================

  const handleNext = () => {
    setCurrentStep(2);
  };

  const handlePrev = () => {
    setCurrentStep(1);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // ============================================
  // Submit Handler
  // ============================================

  const submit = async values => {
    setLoader(true);
    const { branchName, voucherList: originalVoucherList, ...filterPassData } = passData;
    const { title, outline, curImg, fileName, ...filterValues } = values;

    const hasSaleEndDate = values.hasSaleEndDate ?? false;
    if (!hasSaleEndDate && !validateDateRange(filterValues.saleStartDate, filterValues.saleEndDate)) {
      toast.error('기간을 다시 확인해주세요');
      setLoader(false);
      return;
    }

    const voucherList = originalVoucherList;

    const saleEndDateFormatted =
      hasSaleEndDate || !filterValues.saleEndDate || !dayjs(filterValues.saleEndDate).isValid()
        ? ''
        : dayjs(filterValues.saleEndDate).format('YYYY-MM-DD');

    const filteredData = {
      ...filterPassData,
      ...filterValues,
      saleStartDate: dayjs(filterValues.saleStartDate).format('YYYY-MM-DD'),
      saleEndDate: saleEndDateFormatted,
      branchId: user?.branch?.branchId === null ? '' : user?.branch?.branchId,
      hasSaleEndDate,
      classDetail: filterValues.classDetail === undefined ? '' : filterValues.classDetail,
    };

    const formData = new FormData();

    // allow-list: 백엔드 DTO 가 받는 스칼라 필드만 명시 전송한다.
    // (sportType/가격/기간/요일/시간 등 나머지는 아래에서 가공해 개별 append)
    // 구독 설정(subscriptionYn/billingCycle/contractYn)도 productType 기준으로
    // 아래에서 명시적으로 append 하므로 여기 allow-list 에는 포함하지 않는다.
    // #73 판매지점·이용지점 — 합집합 2개 이상이면 공유 상품으로 저장된다
    const saleToSend = (getValues('saleBranchIds') || []).filter(Boolean);
    const useToSend = (getValues('useBranchIds') || []).filter(Boolean);
    // #98 소속지점 모드 — 이용지점 목록 대신 플래그만 보낸다(백엔드가 이용 행을 비운다)
    const useSaleBranch = getValues('useSaleBranchYn') === 'Y';
    formData.append('useSaleBranchYn', useSaleBranch ? 'Y' : 'N');
    (saleToSend.length ? saleToSend : [user?.branch?.branchId]).forEach(bid => {
      formData.append('saleBranchIds', bid);
    });
    if (!useSaleBranch) {
      (useToSend.length ? useToSend : [user?.branch?.branchId]).forEach(bid => {
        formData.append('useBranchIds', bid);
      });
    }

    appendFields(formData, filteredData, [
      'branchId',
      'passName',
      'saleStartDate',
      'saleEndDate',
      'hasSaleEndDate',
      'classDetail',
    ]);

    // Handle images — 새로 올린 파일은 images, 기존/복사본 이미지는 URL 그대로 imageStrings
    const validVouchers = voucherList.filter(voucher => voucher.voucherImg || voucher.voucherUrl);
    if (!isEditMode && validVouchers.length === 0) {
      toast.error('이용권 이미지를 1개 이상 등록 해주세요');
      setLoader(false);
      return;
    }
    validVouchers.forEach(voucher => {
      if (voucher.voucherImg) {
        formData.append('images', voucher.voucherImg);
      } else {
        formData.append('imageStrings', voucher.voucherUrl);
      }
    });

    formData.append('passType', 'GENERAL');
    const passSportsArr = Array.isArray(values.sports)
      ? values.sports
      : values.sports
      ? String(values.sports).split(',').filter(Boolean)
      : [];
    if (passSportsArr.length > 0) {
      formData.append('sportType', passSportsArr[0]); // 대표값(단일)
      appendField(formData, 'sportTypes', passSportsArr); // 복수 종목(키 반복)
    }

    additionalProductSettings.forEach((products, index) => {
      formData.append(`products[${index}].name`, products.name);
      formData.append(`products[${index}].price`, Number(products.price));
      formData.append(`products[${index}].status`, products.use ? 'USED' : 'UNUSED');
    });

    const parsePrice = value => {
      if (value === null || value === undefined) return 0;
      return Number(value.toString().replace(/,/g, ''));
    };
    formData.append('discountRate', parsePrice(values.discountRate));
    formData.append('discountType', discountType === '%' ? 'RATE' : 'AMOUNT');
    formData.append('finalPrice', parsePrice(values.finalPrice));
    formData.append('regularPrice', parsePrice(values.regularPrice));

    if (!isEditMode) {
      formData.append('kioskProduct', values.kiosk === true);
    }
    const isSubscription = productType === 'SUBSCRIPTION';

    // 구독제는 월간 자동결제로 고정 → 이용기간 1개월, 약정 없음
    if (isSubscription) {
      formData.append('usagePeriodType', 'MONTH');
      formData.append('usagePeriod', 1);
    } else {
      formData.append('usagePeriodType', usagePeriodType);
      if (values.usagePeriod) formData.append('usagePeriod', values.usagePeriod);
    }

    // 구독제 설정 매핑 (백엔드 CoursePassCreateRequest) — 월간 고정, 약정 불가
    formData.append('subscriptionYn', isSubscription);
    formData.append('exposureYn', appExposure ? 'Y' : 'N');
    if (isSubscription) {
      formData.append('billingCycle', 'MONTH');
      formData.append('contractYn', false);
    }
    // 횟수제일 경우 usageCount 저장
    if (usageType === 'COUNT' && values.usageCount) {
      formData.append('usageCount', values.usageCount);
    }

    // 요일 설정 (allowedDays) - 중복 제거
    if (selectedDays.length > 0) {
      const uniqueDays = [...new Set(selectedDays)];
      formData.append('allowedDays', uniqueDays.join(','));
    }

    // 시간 설정 (LocalTime 형식: "HH:mm:ss")
    const startTimeStr = `${timeSettings.startHour}:${timeSettings.startMinute}:00`;
    const endTimeStr = `${timeSettings.endHour}:${timeSettings.endMinute}:00`;
    formData.append('startTime', startTimeStr);
    formData.append('endTime', endTimeStr);

    // 중지권: 무제한=빈값(null), 중지불가=0, 직접입력=양수
    const stopType = getValues('stopType');
    if (stopType === 'UNLIMITED') {
      formData.append('stopCount', '');
      formData.append('stopPeriod', '');
    } else if (stopType === 'DISABLED') {
      formData.append('stopCount', 0);
      formData.append('stopPeriod', 0);
    } else {
      formData.append('stopCount', values.stopCount);
      formData.append('stopPeriod', values.stopPeriod);
    }

    // 입장 가능 출입문 — 전환 지점은 항상 반영(빈 선택=전체 해제 플래그), 미전환은 기존대로.
    if (selectedDoorIds.length > 0) {
      selectedDoorIds.forEach(doorId => formData.append('accessGroupIds', doorId));
    } else if (doorBranchConverted) {
      formData.append('clearAccessGroups', 'true');
    }

    // submit 직전 방어: SUBSCRIPTION 상태이나 eligibility가 명시적으로 false인 경우 차단
    if (isSubscription && subscriptionEligibility?.enabled === false) {
      setSubscriptionBlockModalOpen(true);
      setLoader(false);
      return;
    }

    try {
      if (isEditMode) {
        await updatePass(id, formData, user.token);
        toast.success(t('Successfully updated'));
      } else {
        await createPass(formData, user.token);
        toast.success(t('Pass created successfully'));
      }
      navigate('/pass');
    } catch (error) {
      const msg = errorMessages(error);
      // 백엔드가 미승인 에러 메시지를 반환하면 팝업으로 표시
      if (
        typeof msg === 'string' &&
        msg.includes('구독 기능은 승인된 지점에서만 사용할 수 있습니다')
      ) {
        setSubscriptionBlockModalOpen(true);
      } else {
        toast.error(msg);
      }
      setLoader(false);
    }
  };

  // ============================================
  // Status Change Handler (Edit Mode)
  // ============================================

  const changePassStatus = async e => {
    const status = e.target.checked ? 'APPROVED' : 'EXPIRED';
    try {
      await changeStatusPass(status, id, user.token);
      toast.success(t('Pass status Changed'));
      setValue('saleStatus', status);
      if (status === 'EXPIRED') setAppExposure(false);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // ============================================
  // Copy Handler (Edit Mode)
  // ============================================

  const copyPassData = () => {
    navigate(`/pass/create?copyFrom=${id}`);
  };

  // ============================================
  // Delete Handler (Edit Mode)
  // ============================================

  const deletePass = async () => {
    setLoader(true);
    try {
      await deletePassById(id, user.token);
      toast.success(t('Pass Deleted'));
      navigate('/pass');
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  return (
    <>
    <PageContainer>
      {/* 생성 모드 단계 표시 UI */}
      {isCopyMode && (
        <CopyModeBanner>
          <ContentCopyOutlinedIcon sx={{ width: '18px', height: '18px', color: '#3E9FA7' }} />
          <CopyModeBannerTitle>복사본 만드는 중</CopyModeBannerTitle>
          <CopyModeBannerText>
            {copySourceName ? `'${copySourceName}'` : '원본'} 의 설정을 불러왔습니다. 저장해야 새 이용권으로 생성됩니다.
          </CopyModeBannerText>
        </CopyModeBanner>
      )}

      {!isEditMode && (
        <StepIndicatorContainer>
          <StepTabsWrapper>
            <StepTab active={currentStep === 1} zIndex={4}>
              <StepTabShapeFirst active={currentStep === 1} />
              <StepTabContent active={currentStep === 1} isFirst>
                <StepTabNumber active={currentStep === 1}>1</StepTabNumber>
                <StepTabLabel active={currentStep === 1}>상품 정보</StepTabLabel>
              </StepTabContent>
            </StepTab>
            <StepTab active={currentStep === 2} zIndex={3}>
              <StepTabShapeSecond active={currentStep === 2} />
              <StepTabContent active={currentStep === 2}>
                <StepTabNumber active={currentStep === 2}>2</StepTabNumber>
                <StepTabLabel active={currentStep === 2}>상품 설명</StepTabLabel>
              </StepTabContent>
            </StepTab>
          </StepTabsWrapper>
          <HeaderActionGroup>
            <LoadProductButton onClick={() => setPickerOpen(true)} variant="contained" disableElevation>
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
              기존 상품 불러오기
            </LoadProductButton>
            <BackToListLink as={Link} to="/pass">
              <BackToListIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 19L8 12L15 5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </BackToListIcon>
              <BackToListText>목록으로</BackToListText>
            </BackToListLink>
          </HeaderActionGroup>
        </StepIndicatorContainer>
      )}

      <ProductPickerModal
        open={pickerOpen}
        kind="PASS"
        onClose={() => setPickerOpen(false)}
        onSelect={selectedId => {
          setPickerOpen(false);
          navigate(`/pass/create?copyFrom=${selectedId}`);
        }}
      />

      {/* Edit Mode Header */}
      {isEditMode && (
        <>
          <Divider />
          <EditModeHeader>
            <ToggleContainer>
              <ToggleLabel>판매 {watch('saleStatus') === 'APPROVED' ? 'ON' : 'OFF'}</ToggleLabel>
              <StyledSwitch
                checked={watch('saleStatus') === 'APPROVED'}
                onChange={changePassStatus}
                disabled={user.userRole === 'ROLE_MANAGER' && passData.saleStatus === 'APPROVAL_PENDING'}
              />
            </ToggleContainer>

            <PassInfoWrapper>
              <PassInfoHeader>
                <PassInfoTitle>
                  {watch('passName')}{' '}
                  {(Array.isArray(watch('sports')) ? watch('sports') : [watch('sports')])
                    .filter(Boolean)
                    .map(t)
                    .join(', ')}{' '}
                  {`${watch('usagePeriod')}${usagePeriodType === 'MONTH' ? '개월' : '일'}`}
                </PassInfoTitle>
                <StatusBadge variant="outlined">{sellStatus(watch('saleStatus'), t)}</StatusBadge>
                <FavoriteStar targetType={FAVORITE_TARGET.PASS} targetId={Number(id)} favorite={favorite} onChanged={setFavorite} />
              </PassInfoHeader>
              <WishlistWrapper>
                <WishlistIcon src="/images/heart.svg" alt="love" />
                <WishlistText>찜하기 수 {passData.wishListAddedCount ?? 0}</WishlistText>
              </WishlistWrapper>
            </PassInfoWrapper>
            <CopyButton onClick={() => setChangeLogOpen(true)} size="large" sx={{ mr: '8px' }}>
              상품 변경 로그
              <HistoryOutlinedIcon sx={{ width: '16px', height: '16px' }} />
            </CopyButton>
            <CopyButton onClick={() => setCopyModalOpen(true)} size="large">
              이용권 복사
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
            </CopyButton>

            <ProductChangeHistoryModal
              open={changeLogOpen}
              onClose={() => setChangeLogOpen(false)}
              productType="PASS"
              productId={id}
              productName={watch('passName')}
              token={user.token}
            />

            {/* Copy Modal */}
            <Modal open={copyModalOpen} onClose={() => setCopyModalOpen(false)}>
              <ModalContainer>
                <ModalCloseIcon as={GridCloseIcon} onClick={() => setCopyModalOpen(false)} />
                <ModalIconBadge>
                  <ContentCopyOutlinedIcon sx={{ width: '22px', height: '22px', color: '#4AB3BC' }} />
                </ModalIconBadge>
                <ModalContent>
                  <ModalTitle>이 이용권을 복사해 새 상품을 만들까요?</ModalTitle>
                </ModalContent>
                <ModalDescription>
                  <ModalDescText>원본 설정이 채워진 이용권 등록 화면으로 이동합니다.</ModalDescText>
                  <ModalDescTextSub>이용권명을 입력하고 저장해야 새 상품이 생성됩니다.</ModalDescTextSub>
                </ModalDescription>
                <ModalButtonWrapper>
                  <ModalCancelButton onClick={() => setCopyModalOpen(false)} variant="contained">
                    {t('Cancel')}
                  </ModalCancelButton>
                  <ModalConfirmButton onClick={copyPassData} variant="contained">
                    복사해서 만들기
                  </ModalConfirmButton>
                </ModalButtonWrapper>
              </ModalContainer>
            </Modal>
          </EditModeHeader>
          <Divider />
        </>
      )}

      {/* 구독 미승인 지점 안내 팝업 */}
      <Modal open={subscriptionBlockModalOpen} onClose={handleSubscriptionBlockClose}>
        <SubBlockModalContainer>
          <SubBlockCloseButton
            onClick={handleSubscriptionBlockClose}
            role="button"
            aria-label="닫기"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </SubBlockCloseButton>
          <SubBlockIconWrapper>
            <LockOutlinedIcon sx={{ fontSize: 32 }} />
          </SubBlockIconWrapper>
          <SubBlockTitle>구독 기능 미승인 지점</SubBlockTitle>
          <SubBlockBody>
            <SubBlockBodyText>
              구독 기능은 <SubBlockEmphasis>신청·승인된 지점</SubBlockEmphasis>만{'\n'}사용할 수 있습니다.
            </SubBlockBodyText>
            <SubBlockBodyText sx={{ mt: '6px' }}>
              도입을 원하시면 문의해 주세요.
            </SubBlockBodyText>
          </SubBlockBody>
          <SubBlockConfirmButton
            onClick={handleSubscriptionBlockClose}
            variant="contained"
            disableElevation
          >
            확인
          </SubBlockConfirmButton>
        </SubBlockModalContainer>
      </Modal>

      {/* Step Content */}
      {currentStep === 1 ? (
        <PassEditStep01
          control={control}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          user={user}
          branchOptions={directBranches || []}
          legacyBranchNames={legacyBranchNames}
          data={null}
          additionalProductSettings={additionalProductSettings}
          handleAddProduct={handleAddProduct}
          handleRemoveProduct={handleRemoveProduct}
          selectedDays={selectedDays}
          handleDayToggle={handleDayToggle}
          timeSettings={timeSettings}
          handleTimeChange={handleTimeChange}
          usagePeriodType={usagePeriodType}
          setUsagePeriodType={setUsagePeriodType}
          isTaxFree={isTaxFree}
          setIsTaxFree={setIsTaxFree}
          discountType={discountType}
          setDiscountType={setDiscountType}
          hasDiscountEndDate={hasDiscountEndDate}
          setHasDiscountEndDate={setHasDiscountEndDate}
          handleRegularPriceChange={handleRegularPriceChange}
          handleDiscountRateChange={handleDiscountRateChange}
          handleFinalPriceChange={handleFinalPriceChange}
          onNext={handleNext}
          onCancel={handleCancel}
          loader={loader}
          DAYS={DAYS}
          HOURS={HOURS}
          MINUTES={MINUTES}
          centrerInfo={centrerInfo}
          appExposure={appExposure}
          onExposureToggle={handleExposureToggle}
          usageType={usageType}
          setUsageType={setUsageType}
          productType={productType}
          setProductType={handleProductTypeChange}
          isEditMode={isEditMode}
          selectedDoorIds={selectedDoorIds}
          onDoorIdsChange={setSelectedDoorIds}
          onDoorConvertedChange={setDoorBranchConverted}
          copySourceName={copySourceName}
        />
      ) : (
        <PassEditStep02
          control={control}
          setValue={setValue}
          passData={passData}
          setPassData={setPassData}
          curriculumList={curriculumList}
          setCurriculumList={setCurriculumList}
          aiToggle={aiToggle}
          setAiToggle={setAiToggle}
          uploadImages={uploadImages}
          deleteVoucher={deleteVoucher}
          handleCurriculumChange={handleCurriculumChange}
          handleCurriculumImageUpload={handleCurriculumImageUpload}
          handleCurriculumImageDelete={handleCurriculumImageDelete}
          addCurriculum={addCurriculum}
          deleteCurriculum={deleteCurriculum}
          onPrev={handlePrev}
          onSubmit={handleSubmit(submit)}
          onCancel={handleCancel}
          loader={loader}
          isEditMode={isEditMode}
          canSave={canSave}
          exposureYn={appExposure ? 'Y' : 'N'}
          setExposureYn={v => setAppExposure(v === 'Y')}
        />
      )}

      {/* Delete Modal (Edit Mode) */}
      {isEditMode && (
        <BannerDeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} handleDelete={deletePass} />
      )}
    </PageContainer>

    <ConfirmModal
      open={exposureAlertOpen}
      onClose={() => setExposureAlertOpen(false)}
      onConfirm={() => setExposureAlertOpen(false)}
      message={`판매종료된 상품은\n앱 노출이 불가합니다.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />
    </>
  );
};

export default PassEdit;
